import type { ApiEnvelope, ApiErrorBody } from './types';

/**
 * Typed API client.
 *
 * There is ONE base origin, taken from configuration. The admin surface uses the
 * session cookie plus a CSRF token; the player surface uses a bearer token. They
 * are never mixed in the same request.
 */

export const API_ORIGIN: string = import.meta.env.VITE_ASKRS_ORIGIN ?? '';

export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly requestId: string;
  readonly fields: Record<string, string | string[]>;
  readonly extra: Record<string, unknown>;
  readonly retryable: boolean;

  constructor(
    status: number,
    code: string,
    message: string,
    requestId: string,
    fields: Record<string, string | string[]> = {},
    extra: Record<string, unknown> = {},
    retryable = false,
  ) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
    this.requestId = requestId;
    this.fields = fields;
    this.extra = extra;
    this.retryable = retryable;
  }

  /** Maps an error onto the screen state the design system expects. */
  get screenState() {
    switch (this.code) {
      case 'VALIDATION_FAILED':
        return 'VALIDATION_ERROR';
      case 'UNAUTHENTICATED':
      case 'FORBIDDEN':
      case 'MFA_REQUIRED':
      case 'REAUTH_REQUIRED':
        return 'FORBIDDEN';
      case 'NOT_FOUND':
        return 'NOT_FOUND';
      case 'EXPIRED':
      case 'COUPON_EXPIRED':
        return 'EXPIRED';
      case 'REVISION_CONFLICT':
      case 'SAVE_CONFLICT':
      case 'IDEMPOTENCY_KEY_REUSED':
        return 'CONFLICT';
      case 'POLICY_BLOCKED':
        return 'POLICY_BLOCKED';
      case 'PROVIDER_NOT_CONFIGURED':
        return 'NOT_CONFIGURED';
      case 'RATE_LIMITED':
        return 'RATE_LIMITED';
      case 'CAPABILITY_DISABLED':
        return 'UNSUPPORTED';
      case 'SERVICE_UNAVAILABLE':
        return 'ERROR_RETRYABLE';
      case 'NETWORK':
        return 'OFFLINE';
      default:
        return this.retryable ? 'ERROR_RETRYABLE' : 'ERROR_FATAL';
    }
  }
}

let csrfToken: string | null = null;
let playerToken: string | null = null;

export function setPlayerToken(token: string | null) {
  playerToken = token;
}

function readCookie(name: string): string | null {
  const match = document.cookie.match(new RegExp('(^|;\\s*)' + name + '=([^;]*)'));
  return match ? decodeURIComponent(match[2]) : null;
}

/** The admin SPA fetches a CSRF cookie once, then echoes it on every write. */
export async function ensureCsrf(): Promise<void> {
  if (csrfToken) return;
  await fetch(`${API_ORIGIN}/api/admin/v1/csrf`, { credentials: 'include' });
  csrfToken = readCookie('XSRF-TOKEN');
}

interface RequestOptions {
  method?: string;
  body?: unknown;
  surface?: 'public' | 'player' | 'admin' | 'account';
  idempotencyKey?: string;
  signal?: AbortSignal;
  query?: Record<string, string | number | boolean | undefined | null>;
}

export async function apiRequest<T>(path: string, options: RequestOptions = {}): Promise<ApiEnvelope<T>> {
  const { method = 'GET', body, surface = 'public', idempotencyKey, signal, query } = options;

  const url = new URL(`${API_ORIGIN}${path}`, window.location.origin);
  for (const [key, value] of Object.entries(query ?? {})) {
    if (value !== undefined && value !== null && value !== '') {
      url.searchParams.set(key, String(value));
    }
  }

  const headers: Record<string, string> = { Accept: 'application/json' };
  if (body !== undefined) headers['Content-Type'] = 'application/json';
  if (idempotencyKey) headers['Idempotency-Key'] = idempotencyKey;

  if (surface === 'player' && playerToken) {
    headers.Authorization = `Bearer ${playerToken}`;
  }

  if ((surface === 'admin' || surface === 'account') && method !== 'GET') {
    if (surface === 'admin') await ensureCsrf();
    const token = readCookie('XSRF-TOKEN');
    if (token) headers['X-XSRF-TOKEN'] = token;
    headers['X-Requested-With'] = 'XMLHttpRequest';
  }

  let response: Response;
  try {
    response = await fetch(url.toString(), {
      method,
      headers,
      body: body === undefined ? undefined : JSON.stringify(body),
      // Cookies only travel on the session-based surfaces.
      credentials: surface === 'admin' || surface === 'account' ? 'include' : 'same-origin',
      signal,
    });
  } catch (cause) {
    throw new ApiError(0, 'NETWORK', '네트워크 연결 실패', '-', {}, {}, true);
  }

  if (response.status === 204 || response.status === 304) {
    return { data: null as T, meta: { request_id: '-', schema_version: 1, server_time: '' } };
  }

  const payload = await response.json().catch(() => null);

  if (!response.ok) {
    const error = (payload as ApiErrorBody | null)?.error;
    const { code: _c, message: _m, retryable: _r, fields: _f, ...extra } = error ?? {};
    throw new ApiError(
      response.status,
      error?.code ?? 'UNKNOWN',
      error?.message ?? '요청 처리 실패',
      (payload as ApiErrorBody | null)?.meta?.request_id ?? '-',
      (error?.fields as Record<string, string | string[]>) ?? {},
      extra as Record<string, unknown>,
      Boolean(error?.retryable),
    );
  }

  return payload as ApiEnvelope<T>;
}

export const api = {
  public: <T>(path: string, options: Omit<RequestOptions, 'surface'> = {}) =>
    apiRequest<T>(path, { ...options, surface: 'public' }),
  player: <T>(path: string, options: Omit<RequestOptions, 'surface'> = {}) =>
    apiRequest<T>(path, { ...options, surface: 'player' }),
  admin: <T>(path: string, options: Omit<RequestOptions, 'surface'> = {}) =>
    apiRequest<T>(`/api/admin/v1${path}`, { ...options, surface: 'admin' }),
  account: <T>(path: string, options: Omit<RequestOptions, 'surface'> = {}) =>
    apiRequest<T>(`/api/v1/account${path}`, { ...options, surface: 'account' }),
};

/** A stable key so a retried write is recognised as the same request. */
export function newIdempotencyKey(scope: string): string {
  return `${scope}.${crypto.randomUUID()}`;
}
